import React from 'react';
import Link from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { CircularProgress } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import { conversionReportHref } from '../PaymentConversion/PaymentConversionSummary';
import { BotFunnelFiltersInput, useBotFunnel } from './useBotFunnel';
import { ReportUnavailable } from '../ReportUnavailable';
import {
  EmptyFunnel,
  FunnelHelpTooltip,
  HeadlineCard,
  PartialCoverageWarning,
  formatSalesRatio,
} from './shared';
import { userRolesDoPedido } from '@utils/userRolesDoPedido';

type Props = { filters: BotFunnelFiltersInput };

/**
 * Dashboard summary: three numbers and a link.
 *
 * The stage-by-stage drop-off is ten rows and the origin breakdown is a table - that is analysis, and it
 * lives on the conversion screen. What belongs on the root page is whether the bot converts at all.
 *
 * The coverage warning stays here, not only on the detail screen: a producer who never clicks through
 * still needs to know these numbers cover less of the period than the sales cards beside them.
 */
export function BotFunnelSummary({ filters }: Props) {
  const { data, coverage, hasData, isLoading, hasFailed } =
    useBotFunnel(filters);

  return (
    <section className="pb-main">
      <div className="pb-main flex items-center gap-sm">
        <div>
          <Typography variant="h2">Funil do Bot</Typography>
          <Typography className="text-sm text-lightBlack">
            Da primeira conversa até a cobrança paga
          </Typography>
        </div>
        <FunnelHelpTooltip />
        <Link
          href={conversionReportHref({
            periodRange: filters.periodRange,
            startDate: filters.startDate,
            endDate: filters.endDate,
            userRoles: userRolesDoPedido(filters.userRole),
            botConfigIds: filters.botConfigIds,
          })}
          className="text-sm text-primary whitespace-nowrap flex items-center gap-[4px] ml-auto"
        >
          Ver detalhes
          <ArrowForwardIcon fontSize="small" />
        </Link>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center h-[200px]">
          <CircularProgress />
        </div>
      )}

      {!isLoading && hasFailed && <ReportUnavailable />}

      {!isLoading && !hasFailed && !hasData && (
        <EmptyFunnel collectionStartedAt={data.collectionStartedAt} />
      )}

      {!isLoading && !hasFailed && hasData && coverage.kind === 'partial' && (
        <PartialCoverageWarning
          startedAt={coverage.startedAt}
          uncoveredDays={coverage.uncoveredDays}
        />
      )}
      {!isLoading && !hasFailed && hasData && (
        <div className="grid gap-main md:grid-cols-3">
          <HeadlineCard
            title="Conversas iniciadas"
            value={String(data.attemptsOpened.attempts)}
            caption={
              data.attemptsOpened.attempts !== data.attemptsOpened.leads
                ? `de ${data.attemptsOpened.leads} leads — quem volta depois de 6h inicia outra`
                : `de ${data.attemptsOpened.leads} leads`
            }
          />
          <HeadlineCard
            title="Conversão lead → compra"
            value={`${(data.purchaseRate * 100).toFixed(2)}%`}
            caption={
              data.purchasers !== data.paidSales
                ? `${data.purchasers} ${
                    data.purchasers === 1 ? 'comprador' : 'compradores'
                  }, em ${data.paidSales} vendas`
                : `${data.purchasers} ${
                    data.purchasers === 1 ? 'comprador' : 'compradores'
                  }`
            }
          />
          <HeadlineCard
            title="Leads por comprador"
            value={
              data.leadsPerPurchaser
                ? `1 : ${formatSalesRatio(data.leadsPerPurchaser)}`
                : '—'
            }
            caption={
              data.leadsPerPurchaser
                ? `1 comprador a cada ${formatSalesRatio(
                    data.leadsPerPurchaser,
                  )} leads que falam com o bot`
                : 'nenhuma venda paga no período'
            }
          />
        </div>
      )}
    </section>
  );
}
